import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    InjectionToken,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { TinyColor } from '@ctrl/tinycolor';

import {
    animationFrameScheduler,
    fromEvent,
    merge,
    Observable,
    Subject,
} from 'rxjs';
import {
    debounceTime,
    filter,
    finalize,
    map,
    pairwise,
    sampleTime,
    share,
    takeUntil,
    tap,
    throttleTime,
} from 'rxjs/operators';

import {
    AnimationObserver,
    FPS_DELAY,
    maxSideLength,
    MOUSE_THROTTLE,
} from './ui-data-matrix.constants';
import {
    CellInfo,
    CellPosition,
    Coordinate,
    RectOptions,
} from './ui-data-matrix.types';

const PADDING_SIGMA = 4;

/**
 * Partial mouse event definition
 *
 * @interface MouseEventPartial
 */
interface MouseEventPartial {
    offsetX: number;
    offsetY: number;
    clientX: number;
    clientY: number;
}

/**
 *  Mozilla does not preserve the previous event when using 'pairwise'
 *  Any required properties from the mouse event should be added in this map
 * @param ev mouse event source
 */
const partialMouseEvent = (ev: MouseEventPartial) => {
    return ['offsetX', 'offsetY', 'clientX', 'clientY']
        .reduce((obj, property) => {
            obj[property] = (ev as any)[property];
            return obj;
        }, {} as any) as MouseEventPartial;
};

interface IAnimationFrame {
    size: number;
    opacity: number;
    progress: number;
}

export const DATA_MATRIX_BACKGROUND_COLOR_RESOLVER = new InjectionToken<() => string>('Data-Matrix-Background-Color');

const colorCache: Record<string, TinyColor> = {};

@Component({
    selector: 'ui-data-matrix',
    templateUrl: 'ui-data-matrix.component.html',
    styleUrls: ['./ui-data-matrix.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiDataMatrixComponent<T = {}> implements OnInit, AfterViewInit, OnDestroy {
    @Input()
    public set cellCount(value) {
        if (value === this.dimensions.cell.count) { return; }

        this.dimensions.cell.count = value;
        this._trigger.draw.next();
    }
    public get cellCount() {
        return this.dimensions.cell.count || this.data.length;
    }
    @Input()
    public set data(value) {
        if (!(value instanceof Array)) { return; }

        this._data = value || [];
        this._trigger.draw.next();
    }
    public get data() {
        return this._data;
    }
    @Input()
    public border = 1;

    @Input()
    public tooltipShowDelay = 300;

    @Input()
    public set maxSide(value: number) {
        if (this._maxSide === value) { return; }

        this._maxSide = value;
        this._trigger.draw.next();
    }

    @Input()
    public set cellColor(value: (info: CellInfo<T>) => string) {
        if (this._cellColor.toString() === value.toString()) { return; }

        this._cellColor = value;
        this._trigger.draw.next();
    }

    @Input()
    public animationDuration = 1000;

    @Output()
    public cellHover = new EventEmitter<CellInfo>();

    @Output()
    public cellSelect = new EventEmitter<CellInfo>();

    public tooltipOptions: Coordinate = {
        x: 0,
        y: 0,
    };

    public dimensions = {
        rows: 0,
        columns: 0,
        cell: {
            size: 0,
            count: 0,
        },
        width: 0,
        height: 0,
    };

    @ViewChild('canvas', { static: true })
    private _canvasRef!: ElementRef;
    private _canvas!: HTMLCanvasElement;
    @ViewChild('tooltip', { static: true })
    private _tooltipRef!: ElementRef;
    private _tooltip!: HTMLDivElement;

    private _context!: CanvasRenderingContext2D;

    private _data: T[] = [];
    private _window: Window;
    private _isMouseOverCanvas?: boolean;
    private _maxSide = 100;

    private _trigger = {
        draw: new Subject<void>(),
        mouseMove: new Subject<MouseEventPartial>(),
    };

    private _lastMouseMoveEvent: MouseEventPartial | null = null;

    private _destroyed$ = new Subject();

    private _animation = {
        complete: false,
        inProgress: false,
    };

    private get _backgroundColor() {
        return this._backgroundColorResolver();
    }

    constructor(
        public host: ElementRef,
        private _cd: ChangeDetectorRef,
        @Inject(DATA_MATRIX_BACKGROUND_COLOR_RESOLVER)
        @Optional()
        private _backgroundColorResolver: Function,
        @Inject(DOCUMENT)
        _document: Document,
    ) {
        this._backgroundColorResolver =
            this._backgroundColorResolver ||
            (() => 'white');

        this._window = (_document as any)['parentWindow'] ||
            _document.defaultView;

        merge(
            this._trigger.draw,
            fromEvent(this._window, 'resize')
                .pipe(debounceTime(FPS_DELAY)),
            fromEvent(this._window, 'transitionend')
                .pipe(
                    map(ev => ev.srcElement || (ev as any)['originalTarget']),
                    filter(source =>
                        (source.classList && source.classList.contains('mat-sidenav-content')) ||
                        source === this.host.nativeElement ||
                        source === this._canvas,
                    ),
                ),
        ).pipe(
            filter(() => this._canvas != null),
            sampleTime(FPS_DELAY, animationFrameScheduler),
            takeUntil(this._destroyed$),
        ).subscribe(this._drawMatrix);
    }

    ngOnInit() {
        this._canvas = this._canvasRef.nativeElement;
        this._tooltip = this._tooltipRef.nativeElement;

        this._context = this._canvas.getContext('2d')!;
        merge(
            fromEvent<MouseEvent>(this._canvas, 'mousemove'),
            this._trigger.mouseMove,
        )
            .pipe(
                filter(ev => ev !== null),
                map(partialMouseEvent),
                throttleTime(MOUSE_THROTTLE),
                tap((ev: MouseEventPartial) => this._lastMouseMoveEvent = ev),
                pairwise(),
                takeUntil(this._destroyed$),
            )
            .pipe(
                map(([previous, current]) => [
                    this._mapCell(previous), {
                        ...this._mapCell(current),
                        isFocused: true,
                    }],
                ),
                tap(([previous, current]) => {
                    if (previous.index !== current.index) {
                        this._tooltip.hidden = true;
                        this._drawCell(previous);
                    }
                    this._drawCell(current);
                }),
            )
            .pipe(
                tap(([_, current]) => {
                    this._canvas.style.cursor = this.cellSelect.observers.length && current.index < this.cellCount ?
                        'pointer' :
                        'default';
                }),
                tap(([_, current]) => current.index < this.cellCount ? this.cellHover.emit(current) : null),
                sampleTime(this.tooltipShowDelay, animationFrameScheduler),
                filter(_ => !!this._isMouseOverCanvas),
                filter(_ => this._tooltip.childNodes.length > 0),
                filter(([_, current]) => current.index < this.cellCount),
            )
            .subscribe(([_, current]) => this._drawTooltip(current));

        fromEvent<MouseEvent>(this._canvas, 'mouseleave')
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(() => {
                this._tooltip.hidden = true;
                this._isMouseOverCanvas = false;
                this._drawMatrix();
            });

        fromEvent(this._canvas, 'mouseenter')
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(() => {
                this._isMouseOverCanvas = true;
            });

        fromEvent<MouseEvent>(this._canvas, 'click')
            .pipe(
                map(partialMouseEvent),
                map(this._mapCell),
                filter(cell => cell.index < this.cellCount),
                takeUntil(this._destroyed$),
            )
            .subscribe((cell) => this.cellSelect.emit({
                ...cell,
                isFocused: true,
            }));
    }

    ngAfterViewInit() {
        this._trigger.draw.next();
    }

    ngOnDestroy() {
        this._destroyed$.next();
    }

    private _cellColor = (_info: CellInfo<T>): string => _info.isFocused ? 'rgba(255, 0, 0, 1)' : 'rgba(255, 0, 0, .75)';

    private _mapCell = (ev: MouseEventPartial) => this._cellInfo({
        column: Math.floor(ev.offsetX / this.dimensions.cell.size),
        row: Math.floor(ev.offsetY / this.dimensions.cell.size),
    })

    private _traverse(predicate: (info: CellInfo<T>) => void) {
        for (let row = 0; row < this.dimensions.rows; row++) {
            for (let column = 0; column < this.dimensions.columns; column++) {
                const info = this._cellInfo({
                    column,
                    row,
                });

                predicate(info);
            }
        }
    }

    private _drawMatrix = () => {
        [
            this.dimensions.cell.size,
            this.dimensions.columns,
            this.dimensions.rows,
        ] = maxSideLength(
            this.host.nativeElement.clientWidth - PADDING_SIGMA,
            this.host.nativeElement.clientHeight - PADDING_SIGMA,
            this.cellCount,
            0,
            this._maxSide,
        );

        this.dimensions.width = this.dimensions.columns * this.dimensions.cell.size;
        this.dimensions.height = this.dimensions.rows * this.dimensions.cell.size;

        this._canvas.width = this.dimensions.width;
        this._canvas.height = this.dimensions.height;

        this._cd.detectChanges();

        if (this.cellCount > 5000) {
            this._animation.complete = true;
        }

        if (
            !this._animation.complete &&
            !this._animation.inProgress
        ) {
            const frame$ =
                AnimationObserver<IAnimationFrame>(
                    { size: 0, opacity: 0, progress: 0 },
                    { size: this.dimensions.cell.size, opacity: 1, progress: 100 },
                    this.animationDuration,
                )
                    .pipe(
                        share(),
                        tap(() => this._animation.inProgress = true),
                        takeUntil(
                            merge(
                                this._destroyed$,
                                this._trigger.draw,
                            ),
                        ),
                        finalize(() => {
                            this._animation.complete = true;
                            this._animation.inProgress = false;
                        }),
                    );
            this._traverse(info => this._drawCellFrame(frame$, info));
        } else {
            this._traverse(this._drawCell);
        }

        if (
            !this._isMouseOverCanvas ||
            !this._lastMouseMoveEvent
        ) { return; }

        this._trigger.mouseMove.next(this._lastMouseMoveEvent);
    }

    private _drawTooltip = (cell: CellInfo<T>) => {
        this._tooltip.hidden = false;

        const tooltipX = cell.offset.x +
            this.border -
            (this._tooltip.clientWidth - this.dimensions.cell.size) / 2;

        this.tooltipOptions.x = Math.min(Math.max(tooltipX, 0), this._canvas.width - this._tooltip.clientWidth);
        this.tooltipOptions.y = Math.max(this.border, cell.offset.y - this._tooltip.clientHeight - this.border);

        this._tooltip.style.top = `${this.tooltipOptions.y}px`;
        this._tooltip.style.left = `${this.tooltipOptions.x}px`;
    }

    private _drawCell = (info: CellInfo<T>) => {
        if (
            !this._animation.complete ||
            info.index >= this.cellCount
        ) { return; }

        colorCache[this._backgroundColor] = colorCache[this._backgroundColor] || new TinyColor(this._backgroundColor);
        const backgroundBase = colorCache[this._backgroundColor].toRgb();

        const cellColor = this._cellColor(info);
        const brightness = info.isFocused ? 9 : 0;
        const cacheKey = `${cellColor}_${brightness}`;

        if (!colorCache[cacheKey]) {
            const color = new TinyColor(this._cellColor(info));
            const { r, g, b, a } = color.toRgb();

            colorCache[cacheKey] = new TinyColor({
                r: (1 - a) * backgroundBase.r + a * r,
                g: (1 - a) * backgroundBase.g + a * g,
                b: (1 - a) * backgroundBase.b + a * b,
            }).brighten(info.isFocused ? 9 : 0);
        }

        this._drawRect({
            x: info.offset.x,
            y: info.offset.y,
            offset: 0,
            width: this.dimensions.cell.size,
            height: this.dimensions.cell.size,
            border: this.border,
            color: colorCache[cacheKey].toHexString(),
        });
    }

    private _drawCellFrame = (frame$: Observable<IAnimationFrame>, info: CellInfo<T>) => {
        if (info.index >= this.cellCount) { return; }

        frame$
            .subscribe(
                frame => {
                    const cellColor = this._cellColor(info);
                    const cacheKey = `${cellColor}_0`;
                    colorCache[cacheKey] = colorCache[cacheKey] || new TinyColor(this._cellColor(info));

                    const color = this._getColor(
                        frame,
                        colorCache[cacheKey],
                        info,
                    );

                    this._drawRect({
                        x: info.offset.x,
                        y: info.offset.y,
                        offset: 0,
                        width: this.dimensions.cell.size,
                        height: this.dimensions.cell.size,
                        border: this.border,
                        color: color.toHexString(),
                    });
                },
            );
    }

    private _getColor(frame: IAnimationFrame, rgbColor: TinyColor, info: CellInfo<T>) {
        colorCache[this._backgroundColor] = colorCache[this._backgroundColor] || new TinyColor(this._backgroundColor);
        const backgroundColor = colorCache[this._backgroundColor];

        // We are splitting the canvas length in 10 equal parts and we will calculate the cell color in the current frame based on
        // its position and the current animation progress (0-100).
        const progressDecile = frame.progress / 10;
        const columnsPerDecile = this.dimensions.columns / 10;

        // The gradient will cover 30% of the canvas at any frame
        const gradientPosition = {
            start: progressDecile * columnsPerDecile,
            end: (progressDecile + 3) * columnsPerDecile,
        };

        if (info.position.column > gradientPosition.end) {
            return backgroundColor;
        }

        // here we will compute the gradient
        const gradientLength = gradientPosition.end - gradientPosition.start;
        // as it is an horizontal gradient, we will determine the cell opacity based on its X-axis position within the gradient
        const columnXPosWithinGradient = info.position.column - gradientPosition.start;

        const halfRows = Math.floor(this.dimensions.rows / 2);

        // We need to create a mask over the gradient to create the 'stairs' effect:
        // - if we are in the upper half of the canvas, here are the cells we want to render:
        /**
         * 0 0 0 0 0 0 0 0 0 0
         * 1 1 0 0 0 0 0 0 0 0
         * 1 1 1 1 0 0 0 0 0 0
         * 1 1 1 1 1 1 0 0 0 0
         * 1 1 1 1 1 1 1 1 0 0
         * 1 1 1 1 1 1 1 1 1 1 <-- the middle row of the canvas
         * -------
         * 1 - the cells we want to display
         * 0 - the cells we want to hide
         */

        // So we need a gradient ratio between the gradient length and half the number of rows.
        // This will represent how many cells we want to hide based on the row number.
        const gradientRatio = gradientLength / halfRows;

        if (info.position.row < halfRows) {
            // upper half
            if (columnXPosWithinGradient > info.position.row * gradientRatio) {
                return backgroundColor;
            }
        } else {
            // lower half
            // similar logic as in the upper half,
            // only the way we determine the cells we want to hide based on the row number is different
            const hiddenCellsOnCurrentRow = (info.position.row - halfRows) * gradientRatio;
            if (columnXPosWithinGradient > gradientLength - hiddenCellsOnCurrentRow) {
                return backgroundColor;
            }
        }

        return rgbColor;
    }

    private _drawRect = ({ x, y, offset, width, height, border, color }: RectOptions) => {
        this._context.fillStyle = color;
        this._context.fillRect(
            x + border + offset,
            y + border + offset,
            width - border,
            height - border,
        );
    }

    private _cellInfo = (coordinate: CellPosition) => {
        const index = coordinate.column +
            coordinate.row * this.dimensions.columns;

        return {
            position: coordinate,
            offset: {
                x: coordinate.column * this.dimensions.cell.size,
                y: coordinate.row * this.dimensions.cell.size,
            },
            index,
            data: this.data[index],
            isFocused: false,
        } as CellInfo<T>;
    }
}
